import { useState } from 'react'
import { useAuthStore } from '@/store/authStore'
import { Button } from '@/components/ui/button'
import { Sheet, SheetTrigger, SheetContent, SheetClose } from '@/components/ui/sheet'
import { LogOut, User, Menu, Home, Plane, BookOpen, BarChart3, Mail } from 'lucide-react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

export function Header() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const handleLogout = async () => {
    await logout()
    navigate('/auth/login')
  }

  const navItems = [
    { path: '/', icon: Home, label: t('navigation.dashboard', 'Dashboard') },
    { path: '/aircraft', icon: Plane, label: t('navigation.aircraft', 'Aircraft') },
    { path: '/journals', icon: BookOpen, label: t('navigation.journals', 'Journals') },
    { path: '/reports', icon: BarChart3, label: t('navigation.reports', 'Reports') },
  ]

  if (user?.role === 'operator') {
    navItems.push({
      path: '/invitations',
      icon: Mail,
      label: t('navigation.invitations', 'Convites'),
    })
  }

  return (
    <header className="h-16 bg-white border-b border-slate-200 fixed top-0 left-0 right-0 z-50">
      <div className="h-full px-3 sm:px-4 lg:px-6 flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {/* Hamburger menu — visible below lg */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className="lg:hidden p-2 -ml-1 rounded-md hover:bg-slate-100 flex-shrink-0"
                aria-label={t('navigation.menu', 'Menu')}
              >
                <Menu className="w-5 h-5 text-slate-700" />
              </button>
            </SheetTrigger>
            <SheetContent aria-describedby={undefined}>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-primary-700 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">FJ</span>
                </div>
                <span className="text-lg font-semibold text-slate-900">Flight Journal</span>
              </div>
              <nav className="space-y-1">
                {navItems.map((item) => {
                  const Icon = item.icon
                  const isActive =
                    location.pathname === item.path ||
                    (item.path !== '/' && location.pathname.startsWith(item.path))
                  return (
                    <SheetClose asChild key={item.path}>
                      <Link
                        to={item.path}
                        className={cn(
                          'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
                          isActive
                            ? 'bg-primary-50 text-primary-700 font-medium'
                            : 'text-slate-600 hover:bg-slate-50'
                        )}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{item.label}</span>
                      </Link>
                    </SheetClose>
                  )
                })}
              </nav>
            </SheetContent>
          </Sheet>

          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary-700 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-sm sm:text-lg">FJ</span>
          </div>
          <div className="min-w-0">
            <h1 className="text-base sm:text-lg font-semibold text-slate-900 truncate">Flight Journal</h1>
            <p className="text-xs text-slate-500 hidden sm:block">Diário de Bordo Eletrônico</p>
          </div>
        </div>

        <div className="flex items-center gap-1 sm:gap-2 lg:gap-4 flex-shrink-0">
          {user && (
            <>
              <div className="hidden lg:flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-slate-500" />
                <span className="text-slate-700">{user.full_name || user.email}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/profile')}
                className="gap-1.5 px-2 sm:px-3"
              >
                <User className="w-4 h-4" />
                <span className="hidden sm:inline">Perfil</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="gap-1.5 px-2 sm:px-3"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Sair</span>
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  )
}
